'use client';

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useTekstClient } from 'hooks/useTekstClient';
import ActiveFiltersSection from './ActiveFiltersSection';
import FilterGroup from './FilterGroup';
import styles from './FiltersAddOns.module.scss';

/**
 * Filter UI for add-ons page. Displays filters from wiki_categorie via API.
 */
const MAX_VISIBLE_SUBFILTERS = 5;
const isProductStatusFilter = (filter) => filter?.key === 'status';
const isMkgModulesFilter = (filter) => {
    return Boolean(filter?.is_mkg_modules);
};

export default function FiltersAddOns({
    filters = [],
    inline = false,
    showActiveFiltersOnly = false,
    hideActiveFilters = false,
}) {
    const { tekst } = useTekstClient();
    const [expandedFilters, setExpandedFilters] = useState(() => {
        const initial = {};
        filters.forEach((f) => {
            initial[f.id] = isProductStatusFilter(f) || isMkgModulesFilter(f);
        });
        return initial;
    });
    const [expandedSubfilters, setExpandedSubfilters] = useState(() => {
        const initial = {};
        filters.forEach((f) => {
            if (isMkgModulesFilter(f)) initial[f.id] = true;
        });
        return initial;
    });
    const [activeFiltersExpanded, setActiveFiltersExpanded] = useState(true);
    const searchParams = useSearchParams();
    const { doMultipleChanges, resetParams } = useSetSearchParams();

    const selectedBySlug = useMemo(() => {
        const selected = new Map();
        filters.forEach((filter) => {
            const slug = filter.slug ?? 'filter_' + filter.id;
            const param = searchParams.get(slug);
            if (!param?.trim()) {
                selected.set(slug, new Set());
                return;
            }
            selected.set(
                slug,
                new Set(
                    param
                        .split(',')
                        .map((x) => parseInt(x, 10))
                        .filter((n) => !isNaN(n))
                )
            );
        });
        return selected;
    }, [filters, searchParams]);
    const filtersWithOrderedContent = useMemo(() => filters.map((filter) => {
        const subfilters = Array.isArray(filter?.subfilters) ? [...filter.subfilters] : [];
        subfilters.sort((a, b) => (
            String(a?.naam ?? '').localeCompare(String(b?.naam ?? ''), undefined, { sensitivity: 'base' })
        ));

        return {
            ...filter,
            subfilters,
        };
    }), [filters]);

    const getSelectedForSlug = (slug) => selectedBySlug.get(slug) ?? new Set();

    const withPageReset = (change) => ([
        change,
        { fun: 'setParams', params: [{ name: 'page', value: '1' }] },
    ]);

    const activeFilterCount = filters.reduce((count, filter) => {
        const slug = filter.slug ?? 'filter_' + filter.id;
        return count + getSelectedForSlug(slug).size;
    }, 0);

    const activeFiltersList = filters.flatMap((filter) => {
        const slug = filter.slug ?? 'filter_' + filter.id;
        const selected = getSelectedForSlug(slug);
        return Array.from(selected).map((subfilterId) => {
            const sub = filter.subfilters?.find((s) => s.id === subfilterId);
            return {
                subfilterNaam: sub?.naam ?? String(subfilterId),
                slug,
                subfilterId,
            };
        });
    });

    const removeSingleFilter = (slug, subfilterId) => {
        doMultipleChanges(withPageReset(
            { fun: 'deleteParamArrayInt', params: { name: slug, value: subfilterId } }
        ));
    };

    const toggleFilter = (filterId) => {
        setExpandedFilters((prev) => ({
            ...prev,
            [filterId]: !(prev[filterId] ?? false),
        }));
    };

    const toggleSubfiltersExpanded = (filterId) => {
        setExpandedSubfilters((prev) => ({
            ...prev,
            [filterId]: !prev[filterId],
        }));
    };

    const toggleSubfilter = (slug, subfilterId) => {
        const selected = getSelectedForSlug(slug);
        const isSelected = selected.has(subfilterId);
        doMultipleChanges(withPageReset(
            isSelected
                ? { fun: 'deleteParamArrayInt', params: { name: slug, value: subfilterId } }
                : { fun: 'addParamArrayInt', params: { name: slug, value: subfilterId } }
        ));
    };

    if (!filters?.length) return null;
    if (showActiveFiltersOnly && activeFilterCount === 0) return null;

    return (
        <div className={`${styles.filtersContainer} ${inline ? styles.filtersContainerInline : ''}`}>
            {!hideActiveFilters && (
                <ActiveFiltersSection
                    styles={styles}
                    activeFilterCount={activeFilterCount}
                    activeFiltersExpanded={activeFiltersExpanded}
                    onToggleExpanded={() => setActiveFiltersExpanded((prev) => !prev)}
                    activeFiltersList={activeFiltersList}
                    onResetAll={resetParams}
                    onRemoveSingleFilter={removeSingleFilter}
                />
            )}
            {!showActiveFiltersOnly && <span className={styles.filtersTitle}>{tekst('mkgeu:mkgaddons:filters:title')}</span>}
            {!showActiveFiltersOnly && filtersWithOrderedContent.map((filter) => (
                <FilterGroup
                    key={filter.id}
                    styles={styles}
                    filter={filter}
                    isExpanded={expandedFilters[filter.id] ?? false}
                    isSubExpanded={expandedSubfilters[filter.id] ?? false}
                    maxVisibleSubfilters={MAX_VISIBLE_SUBFILTERS}
                    getSelectedForSlug={getSelectedForSlug}
                    onToggleFilter={toggleFilter}
                    onToggleSubfilter={toggleSubfilter}
                    onToggleSubfiltersExpanded={toggleSubfiltersExpanded}
                />
            ))}
        </div>
    );
}
